/**
 * Configuration — Permissions & Access (per Team Member access configurations).
 */

export const PERMISSIONS_ACCESS_ROUTE = '/admin/configuration/permissions-access';

export const TEAM_OPTIONS = [
  'Development Team',
  'Compliance Team',
  'Moderation Team',
  'Support Team',
  'Content Team',
  'Marketing Team',
  'Operations Team',
  'Social Media Team',
  'Quality Assurance Team',
  'Administration Team',
  'Finance Team',
];

/** Positions filtered by Team. */
export const POSITIONS_BY_TEAM = {
  'Development Team': [
    'Full-stack Developer',
    'Front-end Developer',
    'Back-end Developer',
    'QA Tester',
  ],
  'Compliance Team': ['Compliance Officer', 'Compliance Analyst'],
  'Moderation Team': ['Moderation Officer', 'Moderation Lead'],
  'Support Team': ['Support Officer', 'Support Agent', 'Support Lead', 'Support Manager'],
  'Content Team': ['Content Writer', 'Blog Editor'],
  'Marketing Team': ['Social Media Coordinator', 'Marketing Officer'],
  'Operations Team': ['Operations Lead', 'Operations Officer'],
  'Social Media Team': ['Social Media Coordinator', 'Content Writer'],
  'Quality Assurance Team': ['QA Tester', 'QA Lead'],
  'Administration Team': ['Administrator', 'Operations Officer'],
  'Finance Team': ['Accountant', 'Finance Officer'],
};

/** Flat list of all Positions (legacy Team Members bridge). */
export const JOB_TITLE_OPTIONS = [
  ...new Set(Object.values(POSITIONS_BY_TEAM).flat()),
];

export const ACCESS_SCOPE_OPTIONS = [
  { value: 'assigned_records_only', label: 'Assigned Records Only' },
  { value: 'department_records', label: 'Department Records' },
  { value: 'all_module_records', label: 'All Module Records' },
  { value: 'not_applicable', label: 'Not Applicable' },
  // Legacy aliases kept for older preset rows
  { value: 'assigned_records', label: 'Assigned Records Only' },
  { value: 'team_queue', label: 'Department Records' },
  { value: 'related_records', label: 'Department Records' },
  { value: 'all_area_records', label: 'All Module Records' },
];

export const MODULE_ACTION_KEYS = [
  'view',
  'add',
  'edit',
  'assign',
  'review',
  'changeStatus',
  'addNote',
  'generate',
  'download',
  'activate',
  'deactivate',
  'archive',
];

export const MODULE_ACTION_LABELS = {
  view: 'View',
  add: 'Add',
  edit: 'Edit',
  assign: 'Assign',
  review: 'Review',
  changeStatus: 'Change Status',
  addNote: 'Add Note',
  generate: 'Generate',
  download: 'Download',
  activate: 'Activate',
  deactivate: 'Deactivate',
  archive: 'Archive',
};

/**
 * Eligible modules. Archive / Deactivate appear only where flagged.
 * Owner-controlled modules stay hidden unless granted individually.
 */
export const MODULE_OPTIONS = [
  { value: 'customers', label: 'Customers', hasArchive: false, hasDeactivate: false, ownerControlled: false },
  { value: 'businesses', label: 'Businesses', hasArchive: false, hasDeactivate: false, ownerControlled: false },
  { value: 'posts_management', label: 'Posts Management', hasArchive: true, hasDeactivate: false, ownerControlled: false },
  { value: 'reviews_management', label: 'Reviews Management', hasArchive: true, hasDeactivate: false, ownerControlled: false },
  { value: 'messages', label: 'Messages', hasArchive: false, hasDeactivate: false, ownerControlled: false },
  { value: 'quotes', label: 'Quotes', hasArchive: false, hasDeactivate: false, ownerControlled: false },
  { value: 'moderation', label: 'Moderation Queue', hasArchive: false, hasDeactivate: false, ownerControlled: false },
  { value: 'compliance', label: 'Compliance', hasArchive: false, hasDeactivate: false, ownerControlled: false },
  { value: 'fraud_security', label: 'Fraud & Security', hasArchive: false, hasDeactivate: false, ownerControlled: false },
  { value: 'support_tickets', label: 'Support Tickets', hasArchive: false, hasDeactivate: false, ownerControlled: false },
  { value: 'billing_subscriptions', label: 'Billing & Subscriptions', hasArchive: false, hasDeactivate: false, ownerControlled: true },
  { value: 'pricing_plans', label: 'Pricing Plans', hasArchive: false, hasDeactivate: false, ownerControlled: true },
  { value: 'analytics', label: 'Analytics', hasArchive: false, hasDeactivate: false, ownerControlled: true },
  { value: 'reports', label: 'Reports', hasArchive: false, hasDeactivate: false, ownerControlled: true },
  { value: 'content', label: 'Content', hasArchive: false, hasDeactivate: false, ownerControlled: false },
  { value: 'trade_categories_management', label: 'Trade Categories Management', hasArchive: false, hasDeactivate: true, ownerControlled: false },
  { value: 'keywords_management', label: 'Keywords Management', hasArchive: false, hasDeactivate: true, ownerControlled: false },
  { value: 'form_inventory', label: 'Form Inventory', hasArchive: false, hasDeactivate: false, ownerControlled: true },
  { value: 'appeals', label: 'Appeals', hasArchive: false, hasDeactivate: false, ownerControlled: false },
  { value: 'compliance_items_management', label: 'Licences, Badges & Tickets', hasArchive: false, hasDeactivate: false, ownerControlled: false },
  { value: 'team_members', label: 'Team Members', hasArchive: false, hasDeactivate: false, ownerControlled: true },
  { value: 'settings', label: 'Settings', hasArchive: false, hasDeactivate: false, ownerControlled: true },
  {
    value: 'system_logs',
    label: 'System Logs',
    hasArchive: false,
    hasDeactivate: false,
    ownerControlled: true,
    defaultDataAccessScope: 'not_applicable',
    hasExport: false,
  },
  { value: 'configuration', label: 'Configuration', hasArchive: false, hasDeactivate: false, ownerControlled: true },
  {
    value: 'configuration_validation',
    label: 'Configuration — Validation',
    hasArchive: false,
    hasDeactivate: false,
    ownerControlled: true,
    defaultDataAccessScope: 'not_applicable',
  },
  {
    value: 'configuration_notifications',
    label: 'Configuration — Notifications',
    hasArchive: false,
    hasDeactivate: false,
    ownerControlled: true,
    defaultDataAccessScope: 'not_applicable',
  },
  {
    value: 'configuration_limits',
    label: 'Configuration — Limits & Thresholds',
    hasArchive: false,
    hasDeactivate: false,
    ownerControlled: true,
    defaultDataAccessScope: 'not_applicable',
  },
  {
    value: 'configuration_permissions_access',
    label: 'Configuration — Permissions & Access',
    hasArchive: false,
    hasDeactivate: false,
    ownerControlled: true,
    defaultDataAccessScope: 'not_applicable',
  },
  {
    value: 'configuration_retention',
    label: 'Configuration — Retention',
    hasArchive: false,
    hasDeactivate: false,
    ownerControlled: true,
    defaultDataAccessScope: 'not_applicable',
  },
  {
    value: 'configuration_flag',
    label: 'Configuration — Flag',
    hasArchive: false,
    hasDeactivate: false,
    ownerControlled: true,
    defaultDataAccessScope: 'not_applicable',
  },
  { value: 'integrations', label: 'Integrations', hasArchive: false, hasDeactivate: false, ownerControlled: true },
  { value: 'change_history', label: 'Change History', hasArchive: false, hasDeactivate: false, ownerControlled: true },
];

/** Tab-specific System Logs permissions (View only; destination id `processing` = System Activity). */
export const SYSTEM_LOGS_TAB_OPTIONS = [
  { value: 'activity', label: 'Admin Activity' },
  { value: 'processing', label: 'System Activity' },
  { value: 'security', label: 'Security' },
  { value: 'notifications', label: 'Notifications' },
  { value: 'integrations', label: 'Integrations' },
  { value: 'api', label: 'API' },
  { value: 'health', label: 'Health & Errors' },
];

const emptyActions = () => ({
  view: false,
  add: false,
  edit: false,
  assign: false,
  review: false,
  changeStatus: false,
  addNote: false,
  generate: false,
  download: false,
  activate: false,
  deactivate: false,
  archive: false,
});

export function emptyModulePermission(moduleValue = '') {
  const meta = MODULE_OPTIONS.find((m) => m.value === moduleValue);
  return {
    module: moduleValue,
    ...emptyActions(),
    dataAccessScope: meta?.defaultDataAccessScope || (meta ? 'assigned_records_only' : ''),
  };
}

export function getModuleMeta(moduleValue) {
  return MODULE_OPTIONS.find((m) => m.value === moduleValue) || null;
}

export function getModuleLabel(moduleValue) {
  return getModuleMeta(moduleValue)?.label || moduleValue || '—';
}

export function getAccessScopeLabel(value) {
  return ACCESS_SCOPE_OPTIONS.find((o) => o.value === value)?.label || value || '—';
}

export function getPositionsForTeam(team) {
  if (!team) return [];
  return POSITIONS_BY_TEAM[team] || [];
}

export function getPresetNameForPosition(position) {
  if (!position) return '';
  return `${position} Preset`;
}

export function getDefaultAccessLabelForPosition(position) {
  if (!position) return '';
  return `${position} Access`;
}

function modulePermission(module, actions, dataAccessScope = 'assigned_records_only') {
  return {
    module,
    view: Boolean(actions.view),
    add: Boolean(actions.add),
    edit: Boolean(actions.edit),
    assign: Boolean(actions.assign),
    review: Boolean(actions.review),
    changeStatus: Boolean(actions.changeStatus),
    addNote: Boolean(actions.addNote),
    generate: Boolean(actions.generate),
    download: Boolean(actions.download),
    activate: Boolean(actions.activate),
    deactivate: Boolean(actions.deactivate),
    archive: Boolean(actions.archive),
    dataAccessScope,
  };
}

/**
 * Admin / Operational roles:
 * - Not allowed: Billing & Subscriptions, Team Members, Settings, System Logs, Change History
 * - All other modules (incl. Pricing Plans, Form Inventory, Configuration): View only
 * - Analytics & Reports: View only, related-records scope (data limited to allowed modules)
 */
export const OPERATIONAL_ADMIN_DENIED_MODULES = [
  'billing_subscriptions',
  'team_members',
  'settings',
  'system_logs',
  'change_history',
];

const VIEW_ONLY_ACTIONS = { view: true, add: false, edit: false, archive: false, deactivate: false };
const FULL_EDIT_ACTIONS = { view: true, add: true, edit: true, archive: false, deactivate: false };

/**
 * Support Team roles:
 * - Not allowed: Billing & Subscriptions, Pricing Plans, Reports, Content, Trade Categories,
 *   Keywords, Form Inventory, Team Members, Settings, System Logs, Configuration,
 *   Integrations, Change History, Licences/Badges/Tickets
 * - View only: Customers, Businesses, Posts, Reviews, Messages, Quotes
 * - Other allowed modules (e.g. Support Tickets): View / Add / Edit
 * - Analytics: View only, related-records scope (data limited to allowed modules)
 */
export const SUPPORT_TEAM_DENIED_MODULES = [
  'billing_subscriptions',
  'pricing_plans',
  'reports',
  'content',
  'trade_categories_management',
  'keywords_management',
  'form_inventory',
  'configuration',
  'team_members',
  'settings',
  'system_logs',
  'integrations',
  'change_history',
  'compliance_items_management',
];

/**
 * Support Manager:
 * - Not allowed: Billing, Pricing Plans, Content, Trade Categories, Keywords,
 *   Form Inventory, Team Members, Settings, System Logs, Configuration,
 *   Integrations, Change History, Licences/Badges/Tickets
 * - View only: Customers, Businesses, Posts, Reviews, Messages, Quotes
 * - Analytics & Reports: View only, related-records
 */
export const SUPPORT_MANAGER_DENIED_MODULES = [
  'billing_subscriptions',
  'pricing_plans',
  'content',
  'trade_categories_management',
  'keywords_management',
  'form_inventory',
  'configuration',
  'team_members',
  'settings',
  'system_logs',
  'integrations',
  'change_history',
  'compliance_items_management',
];

export const SUPPORT_TEAM_VIEW_ONLY_MODULES = [
  'customers',
  'businesses',
  'posts_management',
  'reviews_management',
  'messages',
  'quotes',
];

export function buildSupportTeamPermissions() {
  const denied = new Set(SUPPORT_TEAM_DENIED_MODULES);
  const viewOnly = new Set(SUPPORT_TEAM_VIEW_ONLY_MODULES);

  return MODULE_OPTIONS.filter((meta) => !denied.has(meta.value)).map((meta) => {
    if (meta.value === 'analytics') {
      return modulePermission(meta.value, VIEW_ONLY_ACTIONS, 'related_records');
    }
    if (viewOnly.has(meta.value)) {
      const scope =
        meta.value === 'customers' || meta.value === 'businesses'
          ? 'assigned_records'
          : meta.value === 'messages' || meta.value === 'quotes'
            ? 'team_queue'
            : 'all_area_records';
      return modulePermission(meta.value, VIEW_ONLY_ACTIONS, scope);
    }
    const scope =
      meta.value === 'support_tickets' ||
      meta.value === 'appeals' ||
      meta.value === 'moderation' ||
      meta.value === 'compliance' ||
      meta.value === 'fraud_security'
        ? 'team_queue'
        : meta.defaultDataAccessScope || 'all_area_records';
    return modulePermission(meta.value, FULL_EDIT_ACTIONS, scope);
  });
}

/**
 * Development Team roles:
 * - Not allowed: Billing & Subscriptions
 * - View only: Pricing Plans, Reports, Team Members, Settings, Integrations
 * - Analytics & Reports: view only, related-records (scoped to allowed modules)
 * - Everything else: full access (view / add / edit)
 */
export function buildDevelopmentTeamPermissions() {
  const denied = new Set(['billing_subscriptions']);
  const viewOnly = new Set([
    'pricing_plans',
    'reports',
    'team_members',
    'settings',
    'integrations',
  ]);

  return MODULE_OPTIONS.filter((meta) => !denied.has(meta.value)).map((meta) => {
    if (meta.value === 'analytics' || meta.value === 'reports') {
      return modulePermission(meta.value, VIEW_ONLY_ACTIONS, 'related_records');
    }
    if (viewOnly.has(meta.value)) {
      return modulePermission(meta.value, VIEW_ONLY_ACTIONS, 'all_area_records');
    }
    return modulePermission(
      meta.value,
      FULL_EDIT_ACTIONS,
      meta.defaultDataAccessScope || 'all_area_records',
    );
  });
}

export function buildSupportManagerPermissions() {
  const denied = new Set(SUPPORT_MANAGER_DENIED_MODULES);
  const viewOnly = new Set(SUPPORT_TEAM_VIEW_ONLY_MODULES);

  return MODULE_OPTIONS.filter((meta) => !denied.has(meta.value)).map((meta) => {
    if (meta.value === 'analytics' || meta.value === 'reports') {
      return modulePermission(meta.value, VIEW_ONLY_ACTIONS, 'related_records');
    }
    if (viewOnly.has(meta.value)) {
      const scope =
        meta.value === 'customers' || meta.value === 'businesses'
          ? 'assigned_records'
          : meta.value === 'messages' || meta.value === 'quotes'
            ? 'team_queue'
            : 'all_area_records';
      return modulePermission(meta.value, VIEW_ONLY_ACTIONS, scope);
    }
    const scope =
      meta.value === 'support_tickets' ||
      meta.value === 'appeals' ||
      meta.value === 'moderation' ||
      meta.value === 'compliance' ||
      meta.value === 'fraud_security'
        ? 'team_queue'
        : meta.defaultDataAccessScope || 'all_area_records';
    return modulePermission(meta.value, FULL_EDIT_ACTIONS, scope);
  });
}

/**
 * Finance Team roles:
 * - Allowed only: Billing & Subscriptions
 * - Not allowed: Pricing Plans (and everything else)
 * - Analytics & Reports: View only, related-records scope
 */
export function buildFinanceTeamPermissions() {
  return [
    modulePermission(
      'billing_subscriptions',
      { ...FULL_EDIT_ACTIONS, download: true },
      'all_area_records'
    ),
    modulePermission('analytics', VIEW_ONLY_ACTIONS, 'related_records'),
    modulePermission('reports', VIEW_ONLY_ACTIONS, 'related_records'),
  ];
}

export function buildOperationalAdminViewOnlyPermissions() {
  const denied = new Set(OPERATIONAL_ADMIN_DENIED_MODULES);
  return MODULE_OPTIONS.filter((meta) => !denied.has(meta.value)).map((meta) => {
    const isRelatedInsights = meta.value === 'analytics' || meta.value === 'reports';
    const scope = isRelatedInsights
      ? 'related_records'
      : meta.defaultDataAccessScope || 'all_area_records';
    return modulePermission(meta.value, VIEW_ONLY_ACTIONS, scope);
  });
}

/** Standard Permission Preset loaded automatically from Position. */
export const PERMISSION_PRESETS_BY_POSITION = {
  'Full-stack Developer': {
    presetName: 'Full-stack Developer Preset',
    defaultAccessLabel: 'Full-stack Developer Access',
    dashboard: 'Development Team Dashboard',
    modulePermissions: buildDevelopmentTeamPermissions(),
  },
  'Front-end Developer': {
    presetName: 'Front-end Developer Preset',
    defaultAccessLabel: 'Front-end Developer Access',
    dashboard: 'Development Team Dashboard',
    modulePermissions: buildDevelopmentTeamPermissions(),
  },
  'Back-end Developer': {
    presetName: 'Back-end Developer Preset',
    defaultAccessLabel: 'Back-end Developer Access',
    dashboard: 'Development Team Dashboard',
    modulePermissions: buildDevelopmentTeamPermissions(),
  },
  'QA Tester': {
    presetName: 'QA Tester Preset',
    defaultAccessLabel: 'QA Tester Access',
    dashboard: 'Quality Assurance Dashboard',
    modulePermissions: [
      modulePermission('posts_management', { view: true }, 'all_area_records'),
      modulePermission('reviews_management', { view: true }, 'all_area_records'),
      modulePermission('support_tickets', { view: true, add: true }, 'team_queue'),
    ],
  },
  'Compliance Officer': {
    presetName: 'Compliance Officer Preset',
    defaultAccessLabel: 'Compliance Officer Access',
    dashboard: 'Compliance Team Dashboard',
    modulePermissions: [
      modulePermission('compliance', { view: true, add: true, edit: true }, 'team_queue'),
      modulePermission('compliance_items_management', { view: true, add: true, edit: true }, 'all_area_records'),
      modulePermission('businesses', { view: true }, 'related_records'),
    ],
  },
  'Compliance Analyst': {
    presetName: 'Compliance Analyst Preset',
    defaultAccessLabel: 'Compliance Analyst Access',
    dashboard: 'Compliance Team Dashboard',
    modulePermissions: [
      modulePermission('compliance', { view: true, edit: true }, 'team_queue'),
      modulePermission('compliance_items_management', { view: true }, 'all_area_records'),
    ],
  },
  'Moderation Officer': {
    presetName: 'Moderation Officer Preset',
    defaultAccessLabel: 'Moderation Officer Access',
    dashboard: 'Moderation Team Dashboard',
    modulePermissions: [
      modulePermission('moderation', { view: true, add: true, edit: true }, 'team_queue'),
      modulePermission('posts_management', { view: true, edit: true, archive: true }, 'all_area_records'),
      modulePermission('reviews_management', { view: true, edit: true, archive: true }, 'all_area_records'),
    ],
  },
  'Moderation Lead': {
    presetName: 'Moderation Lead Preset',
    defaultAccessLabel: 'Moderation Lead Access',
    dashboard: 'Moderation Team Dashboard',
    modulePermissions: [
      modulePermission('moderation', { view: true, add: true, edit: true }, 'all_area_records'),
      modulePermission('posts_management', { view: true, edit: true, archive: true }, 'all_area_records'),
      modulePermission('reviews_management', { view: true, edit: true, archive: true }, 'all_area_records'),
    ],
  },
  'Support Officer': {
    presetName: 'Support Officer Preset',
    defaultAccessLabel: 'Support Officer Access',
    dashboard: 'Support Team Dashboard',
    modulePermissions: buildSupportTeamPermissions(),
  },
  'Support Agent': {
    presetName: 'Support Agent Preset',
    defaultAccessLabel: 'Support Agent Access',
    dashboard: 'Support Team Dashboard',
    modulePermissions: buildSupportTeamPermissions(),
  },
  'Support Lead': {
    presetName: 'Support Lead Preset',
    defaultAccessLabel: 'Support Lead Access',
    dashboard: 'Support Team Dashboard',
    modulePermissions: buildSupportTeamPermissions(),
  },
  'Support Manager': {
    presetName: 'Support Manager Preset',
    defaultAccessLabel: 'Support Manager Access',
    dashboard: 'Support Team Dashboard',
    modulePermissions: buildSupportManagerPermissions(),
  },
  'Content Writer': {
    presetName: 'Content Writer Preset',
    defaultAccessLabel: 'Content Writer Access',
    dashboard: 'Content Team Dashboard',
    modulePermissions: [
      modulePermission('posts_management', { view: true, add: true, edit: true }, 'assigned_records'),
      modulePermission('content', { view: true, add: true, edit: true }, 'assigned_records'),
      modulePermission('keywords_management', { view: true, add: true }, 'all_area_records'),
    ],
  },
  'Blog Editor': {
    presetName: 'Blog Editor Preset',
    defaultAccessLabel: 'Blog Editor Access',
    dashboard: 'Content Team Dashboard',
    modulePermissions: [
      modulePermission('posts_management', { view: true, add: true, edit: true, archive: true }, 'all_area_records'),
      modulePermission('content', { view: true, add: true, edit: true, archive: true }, 'all_area_records'),
      modulePermission('keywords_management', { view: true, add: true, edit: true }, 'all_area_records'),
    ],
  },
  'Social Media Coordinator': {
    presetName: 'Social Media Coordinator Preset',
    defaultAccessLabel: 'Social Media Coordinator Access',
    dashboard: 'Marketing Team Dashboard',
    modulePermissions: [
      modulePermission('posts_management', { view: true, add: true, edit: true }, 'assigned_records'),
      modulePermission('messages', { view: true, add: true }, 'assigned_records'),
    ],
  },
  'Marketing Officer': {
    presetName: 'Marketing Officer Preset',
    defaultAccessLabel: 'Marketing Officer Access',
    dashboard: 'Marketing Team Dashboard',
    modulePermissions: [
      modulePermission('posts_management', { view: true, add: true, edit: true }, 'all_area_records'),
      modulePermission('analytics', { view: true }, 'all_area_records'),
    ],
  },
  'Operations Lead': {
    presetName: 'Operations Lead Preset',
    defaultAccessLabel: 'Operations Lead Access',
    dashboard: 'Operations Team Dashboard',
    modulePermissions: buildOperationalAdminViewOnlyPermissions(),
  },
  'Operations Officer': {
    presetName: 'Operations Officer Preset',
    defaultAccessLabel: 'Operations Officer Access',
    dashboard: 'Operations Team Dashboard',
    modulePermissions: buildOperationalAdminViewOnlyPermissions(),
  },
  'QA Lead': {
    presetName: 'QA Lead Preset',
    defaultAccessLabel: 'QA Lead Access',
    dashboard: 'Quality Assurance Dashboard',
    modulePermissions: [
      modulePermission('posts_management', { view: true, edit: true }, 'all_area_records'),
      modulePermission('support_tickets', { view: true, add: true, edit: true }, 'all_area_records'),
    ],
  },
  Administrator: {
    presetName: 'Administrator Preset',
    defaultAccessLabel: 'Administrator Access',
    dashboard: 'Administration Dashboard',
    modulePermissions: buildOperationalAdminViewOnlyPermissions(),
  },
  Accountant: {
    presetName: 'Accountant Preset',
    defaultAccessLabel: 'Accountant Access',
    dashboard: 'Finance Team Dashboard',
    modulePermissions: buildFinanceTeamPermissions(),
  },
  'Finance Officer': {
    presetName: 'Finance Officer Preset',
    defaultAccessLabel: 'Finance Officer Access',
    dashboard: 'Finance Team Dashboard',
    modulePermissions: buildFinanceTeamPermissions(),
  },
};

export function getPermissionPresetForPosition(position) {
  if (!position) return null;
  const preset = PERMISSION_PRESETS_BY_POSITION[position];
  if (preset) return { ...preset, modulePermissions: cloneModulePermissions(preset.modulePermissions) };
  return {
    presetName: getPresetNameForPosition(position),
    defaultAccessLabel: getDefaultAccessLabelForPosition(position),
    dashboard: 'Team Dashboard',
    modulePermissions: [],
  };
}

export function cloneModulePermissions(rows) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    module: row.module || '',
    view: Boolean(row.view),
    add: Boolean(row.add),
    edit: Boolean(row.edit),
    assign: Boolean(row.assign),
    review: Boolean(row.review),
    changeStatus: Boolean(row.changeStatus),
    addNote: Boolean(row.addNote),
    generate: Boolean(row.generate),
    download: Boolean(row.download),
    activate: Boolean(row.activate),
    deactivate: Boolean(row.deactivate),
    archive: Boolean(row.archive),
    dataAccessScope: row.dataAccessScope || '',
  }));
}

export function normalizeModulePermissions(rows) {
  const map = new Map();
  cloneModulePermissions(rows).forEach((row) => {
    if (!row.module) return;
    map.set(row.module, row);
  });
  return [...map.values()];
}

/** Every MODULE_OPTIONS row, merging granted permissions (missing = no access). */
export function expandModulePermissionsToAllModules(rows) {
  const map = new Map(
    normalizeModulePermissions(rows).map((row) => [row.module, row]),
  );
  return MODULE_OPTIONS.map((meta) => {
    const existing = map.get(meta.value);
    return existing || emptyModulePermission(meta.value);
  });
}

/** Module labels mentioned in saved “permissions added/removed” lines. */
export function extractModuleLabelsFromPermissionLines(lines = []) {
  return [...new Set(
    (lines || [])
      .map((item) => {
        const match = String(item || '').match(/^([^:—]+)/);
        return match ? match[1].trim() : '';
      })
      .filter(Boolean),
  )];
}

/** Union of live diff modules and any previously saved customisation labels. */
export function resolveCustomisedModuleLabels({
  changedModules = [],
  permissionsAdded = [],
  permissionsRemoved = [],
} = {}) {
  return [...new Set([
    ...(changedModules || []),
    ...extractModuleLabelsFromPermissionLines([
      ...(permissionsAdded || []),
      ...(permissionsRemoved || []),
    ]),
  ])];
}

/** Apply permission dependencies when toggling an action. */
export function applyModuleActionToggle(row, actionKey, checked) {
  const next = { ...row };
  const meta = getModuleMeta(row.module);

  if (actionKey === 'view') {
    next.view = checked;
    if (!checked) {
      next.add = false;
      next.edit = false;
      next.archive = false;
      next.deactivate = false;
    }
    return next;
  }

  if (actionKey === 'add') {
    next.add = checked;
    if (checked) next.view = true;
    return next;
  }

  if (actionKey === 'edit') {
    next.edit = checked;
    if (checked) next.view = true;
    if (!checked) {
      next.archive = false;
      next.deactivate = false;
    }
    return next;
  }

  if (actionKey === 'archive') {
    if (!meta?.hasArchive) {
      next.archive = false;
      return next;
    }
    next.archive = checked;
    if (checked) {
      next.edit = true;
      next.view = true;
    }
    return next;
  }

  if (actionKey === 'deactivate') {
    if (!meta?.hasDeactivate) {
      next.deactivate = false;
      return next;
    }
    next.deactivate = checked;
    if (checked) {
      next.edit = true;
      next.view = true;
    }
    return next;
  }

  if (MODULE_ACTION_KEYS.includes(actionKey)) {
    next[actionKey] = checked;
    if (checked) next.view = true;
    return next;
  }

  return next;
}

export function moduleHasAnyPermission(row) {
  if (!row) return false;
  return MODULE_ACTION_KEYS.some((key) => Boolean(row[key]));
}

export function formatModuleActionsSummary(row) {
  if (!row || !moduleHasAnyPermission(row)) return 'No access';
  const meta = getModuleMeta(row.module);
  const parts = MODULE_ACTION_KEYS.filter((key) => {
    if (!row[key]) return false;
    if (key === 'archive' && !meta?.hasArchive) return false;
    if (key === 'deactivate' && !meta?.hasDeactivate) return false;
    return true;
  }).map((key) => MODULE_ACTION_LABELS[key]);
  return parts.join(', ') || 'No access';
}

export function formatModulePermissionsSummary(modulePermissions) {
  const granted = normalizeModulePermissions(modulePermissions).filter(moduleHasAnyPermission);
  if (!granted.length) return '—';
  return granted.map((row) => getModuleLabel(row.module)).join(', ');
}

export function compareModulePermissions(defaultRows, currentRows) {
  const defaults = normalizeModulePermissions(defaultRows);
  const current = normalizeModulePermissions(currentRows);
  const defaultMap = new Map(defaults.map((r) => [r.module, r]));
  const currentMap = new Map(current.map((r) => [r.module, r]));
  const allModules = new Set([...defaultMap.keys(), ...currentMap.keys()]);

  const added = [];
  const removed = [];
  const changedModules = [];

  allModules.forEach((module) => {
    const def = defaultMap.get(module);
    const cur = currentMap.get(module);
    const defGranted = moduleHasAnyPermission(def);
    const curGranted = moduleHasAnyPermission(cur);
    const label = getModuleLabel(module);

    if (!defGranted && curGranted) {
      added.push(label);
      if (label) changedModules.push(label);
    } else if (defGranted && !curGranted) {
      removed.push(label);
      if (label) changedModules.push(label);
    }
  });

  return {
    added,
    removed,
    changedModules,
    hasCustomisedAccess: added.length > 0 || removed.length > 0,
  };
}

/** Display label for Permission name + Customised access. */
export function formatCustomisedAccessDisplay({
  hasCustomisedAccess,
  defaultAccessLabel,
  moduleLabels = [],
} = {}) {
  const permissionName = defaultAccessLabel || '—';
  if (!hasCustomisedAccess) return permissionName;
  const unique = [...new Set((moduleLabels || []).filter(Boolean))];
  if (!unique.length) return `${permissionName} + Customised Access`;
  return `${permissionName} + Customised Access (${unique.join(', ')})`;
}

export function buildAccessSummaryLabel({
  position,
  defaultAccessLabel,
  hasCustomisedAccess,
  customPermission,
} = {}) {
  const base = defaultAccessLabel || (position ? `${position} Access` : '');
  if (!base) return 'Not Assigned';
  if (customPermission && String(customPermission).trim() && customPermission !== 'Customised') {
    return `${base} + Custom Permission (${String(customPermission).trim()})`;
  }
  if (hasCustomisedAccess || customPermission) {
    return `${base} + Custom Permission`;
  }
  return base;
}

export function formatPermissionDateDisplay(value) {
  if (!value || !String(value).trim()) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return value;
  }
}

export function emptyAccessForm() {
  return {
    memberInternalId: '',
    teamMemberId: '',
    name: '',
    workEmail: '',
    team: '',
    position: '',
    teamType: '',
    department: '',
    jobRole: '',
    platformRole: 'Team Member',
    permissionConfigId: null,
    savedPermissionConfigId: null,
    permissionPreset: '',
    defaultAccessLabel: '',
    dashboard: '',
    hasCustomisedAccess: false,
    presetModulePermissions: [],
    modulePermissions: [],
    configurationMaintenance: [],
    accountStatus: '',
    lastUpdated: '',
  };
}

export function accessFormFromMember(member) {
  if (!member) return emptyAccessForm();
  const position = member.jobRole || member.position || '';
  const preset = getPermissionPresetForPosition(position);
  const modulePermissions = normalizeModulePermissions(
    member.modulePermissions?.length
      ? member.modulePermissions
      : preset?.modulePermissions || [],
  );
  const diff = compareModulePermissions(preset?.modulePermissions || [], modulePermissions);
  const defaultAccessLabel =
    member.defaultAccessLabel || preset?.defaultAccessLabel || getDefaultAccessLabelForPosition(position);

  return {
    memberInternalId: member.id || '',
    teamMemberId: member.teamMemberId || '',
    name: member.name || '',
    workEmail: member.workEmail || '',
    team: member.department || member.team || '',
    position,
    teamType: member.teamType || '',
    department: member.department || member.team || '',
    jobRole: position,
    platformRole: 'Team Member',
    permissionConfigId: member.permissionConfigId || null,
    savedPermissionConfigId: member.permissionConfigId || null,
    permissionPreset: member.permissionPreset || preset?.presetName || getPresetNameForPosition(position),
    defaultAccessLabel,
    dashboard: member.dashboard || preset?.dashboard || '',
    hasCustomisedAccess: Boolean(member.hasCustomisedAccess ?? diff.hasCustomisedAccess),
    permissionsAdded: member.permissionsAdded || diff.added,
    permissionsRemoved: member.permissionsRemoved || diff.removed,
    presetModulePermissions: preset?.modulePermissions || [],
    modulePermissions,
    configurationMaintenance: member.configurationMaintenance || [],
    accountStatus: member.status || '',
    lastUpdated: member.lastUpdated || '',
  };
}

export function memberHasSavedAccessConfiguration(member) {
  if (!member || member.isOwner || member.isCoOwner) return false;
  if (member.status === 'Pending Access') return false;
  const team = member.team;
  const position = member.position;
  if (
    !team ||
    team === 'Not Assigned' ||
    team === 'Not Applicable' ||
    !position ||
    position === 'Not Assigned' ||
    position === 'Not Applicable'
  ) {
    return false;
  }
  return true;
}

export function getConfiguredAccessMembers(members) {
  return (members || []).filter(memberHasSavedAccessConfiguration);
}

export function getPendingAccessMembers(members) {
  return (members || []).filter(
    (m) =>
      m &&
      !m.isOwner &&
      !m.isCoOwner &&
      m.status === 'Pending Access' &&
      m.platformRole !== 'Owner' &&
      m.platformRole !== 'Platform Owner' &&
      m.platformRole !== 'Co-owner',
  );
}

/** @deprecated Legacy Standard Permission helpers — kept for Team Members bridges. */
export const PROTECTED_PERMISSION_NAMES = ['Owner Permission', 'Co-owner Permission'];

export function buildSamplePermissions() {
  return Object.entries(PERMISSION_PRESETS_BY_POSITION).map(([position, preset], index) => ({
    id: `PER-${String(index + 1).padStart(4, '0')}`,
    permissionName: preset.defaultAccessLabel,
    status: 'active',
    protected: false,
    jobTitles: [position],
    permissionMatrix: normalizeModulePermissions(preset.modulePermissions).map((row) => ({
      permissionArea: row.module,
      accessScope: row.dataAccessScope,
      allowedActions: MODULE_ACTION_KEYS.filter((k) => row[k]),
      additionalAreaSettings: {},
    })),
    applied: {
      permissionName: preset.defaultAccessLabel,
      status: 'active',
      jobTitles: [position],
      permissionMatrix: [],
    },
    lastSavedOn: '2026-07-01T10:00:00.000Z',
    lastAppliedOn: '2026-07-01T10:00:00.000Z',
  }));
}

export function getPermissionById(id, permissions) {
  return (permissions || buildSamplePermissions()).find((p) => p.id === id);
}

export function getAppliedSnapshot(permission) {
  return permission?.applied || null;
}

export function isPermissionOperationallyActive(permission) {
  return String(permission?.status || '').toLowerCase() === 'active';
}

export function getActiveAssignablePermissions(jobTitle, permissions) {
  return (permissions || buildSamplePermissions()).filter((p) => {
    if (!isPermissionOperationallyActive(p)) return false;
    return (p.jobTitles || []).includes(jobTitle);
  });
}

export function isPermissionActive(permissionId, permissions) {
  const permission = getPermissionById(permissionId, permissions);
  return isPermissionOperationallyActive(permission);
}

/** Screen 1 — Permission Configurations list (paired with Team Member assignment elsewhere). */
export const CONFIGURATION_STATUS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

export const PERMISSIONS_LIST_PREVIEW_COUNT = 3;

export const DEFAULT_DATA_ACCESS_SCOPE_OPTIONS = [
  { value: 'assigned_records_only', label: 'Assigned Records Only' },
  { value: 'department_records', label: 'Department Records' },
  { value: 'all_module_records', label: 'All Module Records' },
  { value: 'not_applicable', label: 'Not Applicable' },
];

export function getDefaultDataAccessScopeLabel(value) {
  return (
    DEFAULT_DATA_ACCESS_SCOPE_OPTIONS.find((o) => o.value === value)?.label ||
    ACCESS_SCOPE_OPTIONS.find((o) => o.value === value)?.label ||
    value ||
    '—'
  );
}

export function formatPermissionDateOnly(value) {
  if (!value || !String(value).trim()) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

/** @deprecated Use buildSamplePermissionConfigs from permissionsAccessMatrix.js */
export function buildSamplePermissionConfigsLegacyStub() {
  return [];
}

/** Permissions column: first 1–3 names · (View All) — Default Scope */
export function formatPermissionsColumnParts(config) {
  const permissions = Array.isArray(config?.permissions) ? config.permissions : [];
  const scopeLabel = getDefaultDataAccessScopeLabel(config?.defaultDataAccessScope);
  const showViewAll = permissions.length > PERMISSIONS_LIST_PREVIEW_COUNT;
  const previewNames = showViewAll
    ? permissions.slice(0, PERMISSIONS_LIST_PREVIEW_COUNT)
    : permissions;
  return {
    permissions,
    previewNames,
    showViewAll,
    scopeLabel,
    previewText: `${previewNames.join(' · ')}${showViewAll ? ' · View All' : ''} — ${scopeLabel}`,
  };
}
